import React from 'react';
import { Button } from '@/components/atoms';
import { Icon } from '@iconify/react';

const BundleToggle = ({ includeMentoring, onToggle, mentoringCount, disabled }) => {
  return (
    <div className="flex flex-col gap-3 p-4 bg-white/10 rounded-lg border border-white/20">
      <h4 className="text-white font-medium text-lg">Pilih Paket Pembelajaran:</h4>

      <div className="flex flex-col gap-2">
        {/* Learning Path Only Option */}
        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="radio"
            name="bundle-option"
            checked={!includeMentoring}
            onChange={() => onToggle(false)}
            disabled={disabled}
            className="w-4 h-4 text-[#05d172] bg-white border-2 border-white focus:ring-[#05d172] focus:ring-2"
          />
          <span className="text-white text-md">Learning Path saja</span>
        </label>

        {/* Bundle with Mentoring Option */}
        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="radio"
            name="bundle-option"
            checked={includeMentoring}
            onChange={() => onToggle(true)}
            disabled={disabled}
            className="w-4 h-4 text-[#05d172] bg-white border-2 border-white focus:ring-[#05d172] focus:ring-2"
          />
          <div className="flex flex-col">
            <span className="text-white text-md">Bundle dengan Mentoring</span>
            {mentoringCount > 0 && (
              <span className="text-purple-200 text-sm">+{mentoringCount} sesi mentoring</span>
            )}
          </div>
        </label>
      </div>
    </div>
  );
};

const PurchaseBanner = ({
  bannerConfig,
  pricing,
  includeMentoring,
  onToggleMentoring,
  onBuyAction,
  isLoading,
  showToggle = false,
  toggleDisabled = false
}) => {
  const formatCurrency = (amount) => {
    const numAmount = Number(amount);
    if (isNaN(numAmount) || numAmount <= 0) {
      return 'Rp 0';
    }
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(numAmount);
  };

  if (!bannerConfig.show) {
    return null;
  }

  return (
    <div className="flex flex-col gap-4 p-4 sm:px-6 sm:py-4 w-full bg-[#5d399a] rounded-2xl overflow-hidden relative">
      {/* Toggle Section - Only show when specified */}
      {showToggle && (
        <BundleToggle
          includeMentoring={includeMentoring}
          onToggle={onToggleMentoring}
          mentoringCount={pricing.mentoringCount}
          disabled={toggleDisabled || isLoading}
        />
      )}

      {/* Content Section */}
      <div className="flex flex-col sm:flex-row sm:items-start gap-4 sm:gap-6 relative flex-1 min-w-0">
        {/* Icon */}
        <div className="hidden md:inline-flex items-center gap-2.5 p-2 bg-[#faf9fb] rounded-xl self-start sm:self-auto flex-shrink-0">
          <Icon
            icon="material-symbols:shopping-cart-outline"
            className="w-5 h-5 sm:w-6 sm:h-6 text-[#8553d8]"
          />
        </div>

        {/* Text Content */}
        <div className="flex flex-col items-start justify-center gap-2 sm:gap-3 flex-1 min-w-0">
          {/* Main Title */}
          <h3 className="[font-family:'Inter',Helvetica] font-medium text-white text-base sm:text-lg leading-5 sm:leading-6 break-words">
            {bannerConfig.title}
          </h3>

          {/* Subtitle */}
          {bannerConfig.subtitle && (
            <p className="text-sm text-purple-200 break-words">
              {bannerConfig.subtitle}
            </p>
          )}

          {/* Price Section */}
          {pricing.finalPrice > 0 && (
            <div className="flex flex-col md:flex-row items-start md:items-center gap-3 w-full">
              {/* Show original price if there are any discounts */}
              {pricing.hasDiscounts && pricing.originalPrice > pricing.finalPrice && (
                <span className="[font-family:'Inter',Helvetica] font-normal text-[#bdbbbe] text-base leading-5 line-through">
                  {formatCurrency(pricing.originalPrice)}
                </span>
              )}
              <span className="[font-family:'Raleway',Helvetica] font-bold text-white text-2xl leading-6 sm:leading-8">
                {formatCurrency(pricing.finalPrice)}
              </span>
            </div>
          )}

          {/* Discount Breakdown */}
          {pricing.hasDiscounts && (
            <div className="text-sm text-purple-200 break-words w-full">
              {pricing.directDiscount > 0 && (
                <span>Diskon langsung {formatCurrency(pricing.directDiscount)}</span>
              )}
              {pricing.directDiscount > 0 && pricing.extraDiscount > 0 && <span> + </span>}
              {pricing.extraDiscount > 0 && (
                <span>Diskon ekstra {formatCurrency(pricing.extraDiscount)}</span>
              )}
            </div>
          )}

          {/* Total Savings */}
          {pricing.totalSavings > 0 && (
            <div className="text-sm text-purple-200 break-words w-full font-medium">
              Total hemat hingga {formatCurrency(pricing.totalSavings)}
            </div>
          )}
        </div>

        {/* Decorative circles */}
        <div className="absolute w-[792px] h-[792px] top-[-147px] left-[457px] bg-[#ffffff1a] rounded-[396px] hidden xl:block pointer-events-none" />
        <div className="absolute w-[792px] h-[792px] top-[-132px] left-[518px] bg-[#ffffff1a] rounded-[396px] hidden xl:block pointer-events-none" />
      </div>

      {/* Button Section */}
      <div className="flex justify-center sm:justify-end w-full sm:w-auto">
        <Button
          variant={Button.VARIANTS.TERTIARY}
          className="w-full sm:w-auto px-6 sm:px-10 py-3 bg-[#05d172] z-10 rounded-[40px] [font-family:'Raleway',Helvetica] font-semibold text-[#231838] text-sm leading-4 min-w-0 flex-shrink-0"
          onClick={() => onBuyAction(bannerConfig.buttonAction)}
          disabled={isLoading}
        >
          <span className="truncate">
            {isLoading ? 'Memproses...' : bannerConfig.buttonText}
          </span>
        </Button>
      </div>
    </div>
  );
};

export default PurchaseBanner;